<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  deliverySummary: {
    type: Object,
    default: () => ({}),
  },
  deliveryLogs: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['edit', 'delete', 'start', 'pause']);

const { t } = useI18n();
const showDeliveryLog = ref(false);

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (!props.isEnabled && !props.scheduledAt) return 'Aguardando início';
  if (!props.isEnabled) return 'Pausada';

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  if (props.status === STATUS_PROCESSING) {
    return t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING');
  }

  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});

const hasDeliveryData = computed(() => props.deliverySummary.total > 0);
const deliveryText = computed(() => {
  const summary = props.deliverySummary;
  return `Enviado à Meta: ${summary.sent || 0} · Entregue: ${summary.delivered || 0} · Lido: ${summary.read || 0} · Falhou: ${summary.failed || 0} · Na fila: ${summary.queued || 0}`;
});
const failedLogs = computed(() => props.deliveryLogs.filter(log => log.status === 'failed' || log.status === 'skipped'));
const isWaitingToStart = computed(() => !props.isEnabled && !props.scheduledAt);
const canPause = computed(() => !props.isLiveChatType && props.isEnabled && (props.status !== STATUS_COMPLETED || props.deliverySummary.queued > 0));
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
      <template v-if="!isLiveChatType && hasDeliveryData">
        <button
          class="text-xs text-left text-n-slate-11 hover:text-n-blue-11"
          type="button"
          @click="showDeliveryLog = !showDeliveryLog"
        >
          {{ deliveryText }} · {{ showDeliveryLog ? 'Ocultar detalhes' : 'Ver falhas' }}
        </button>
        <div v-if="showDeliveryLog" class="w-full max-h-36 overflow-auto text-xs rounded bg-n-alpha-2 p-2">
          <p v-if="failedLogs.length === 0" class="text-n-teal-11">Nenhuma falha reportada pela Meta até agora.</p>
          <p v-for="log in failedLogs" :key="`${log.phone_number}-${log.created_at}`" class="text-n-ruby-11 mb-1">
            {{ log.contact_name || log.phone_number }}: {{ log.error || 'Falha sem detalhe retornado pela Meta' }}
          </p>
        </div>
      </template>
    </div>
    <div class="flex items-center justify-end w-20 gap-2">
      <Button
        v-if="isWaitingToStart"
        variant="solid"
        color="blue"
        size="sm"
        label="Iniciar"
        @click="emit('start')"
      />
      <Button
        v-if="canPause"
        variant="faded"
        color="slate"
        size="sm"
        label="Pausar"
        @click="emit('pause')"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
